import { Directive, Input, TemplateRef, ViewContainerRef, effect, signal } from '@angular/core';
import { AccessCacheService } from '../services/access-cache.service';
import { OrgContextService } from '../services/org-context.service';
import { PermissionsService } from '../services/permissions.service';
import { PermissionMatrixStore } from '../services/permission-matrix.store';
import { PermissionKey } from './permission.types';

/**
 * Conditionally renders the host template for a permission key and optional resource.
 * When the permission fails, the provided fallback template (if any) is rendered instead.
 * Access is re-evaluated whenever the organization role, resource cache version, or inputs change.
 */
@Directive({
  selector: '[appCan]',
  standalone: true
})
export class AppCanDirective {
  private permissionKey = signal<PermissionKey | null>(null);
  private resourceId = signal<string | undefined>(undefined);
  private fallbackTemplate = signal<TemplateRef<any> | null>(null);
  private showingPrimary = false;
  private showingFallback = false;
  private evaluationId = 0;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissions: PermissionsService,
    private orgContext: OrgContextService,
    private accessCache: AccessCacheService,
    private matrixStore: PermissionMatrixStore
  ) {
    effect(() => {
      const permission = this.permissionKey();
      const resourceId = this.resourceId();
      const fallbackTemplate = this.fallbackTemplate();
      const currentEvaluation = ++this.evaluationId;
      this.orgContext.orgRole();
      this.accessCache.version();
      this.matrixStore.version();
      this.matrixStore.currentOrgId();

      if (!permission) {
        this.clearView();
        return;
      }

      this.clearView();
      this.permissions.canAsync(permission, resourceId).then((can) => {
        if (currentEvaluation !== this.evaluationId) {
          return;
        }

        if (can) {
          this.showPrimary();
          return;
        }

        if (fallbackTemplate) {
          this.showFallback(fallbackTemplate);
        }
      }).catch(() => {
        if (currentEvaluation !== this.evaluationId || !fallbackTemplate) {
          return;
        }
        this.showFallback(fallbackTemplate);
      });
    });
  }

  @Input('appCan')
  set appCan(value: PermissionKey) {
    this.permissionKey.set(value);
  }

  @Input()
  set appCanResourceId(value: string | undefined) {
    this.resourceId.set(value);
  }

  @Input()
  set appCanFallback(value: TemplateRef<any> | null) {
    this.fallbackTemplate.set(value);
  }

  /** Show the host template when the permission check passes. */
  private showPrimary(): void {
    if (this.showingPrimary) return;
    this.viewContainer.clear();
    this.viewContainer.createEmbeddedView(this.templateRef);
    this.showingPrimary = true;
    this.showingFallback = false;
  }

  /** Display the fallback template when access is denied. */
  private showFallback(fallback?: TemplateRef<any> | null): void {
    if (this.showingFallback) return;
    this.viewContainer.clear();
    const template = fallback ?? this.fallbackTemplate();
    if (template) {
      this.viewContainer.createEmbeddedView(template);
      this.showingFallback = true;
    } else {
      this.showingFallback = false;
    }
    this.showingPrimary = false;
  }

  /** Clear both primary and fallback templates from the view. */
  private clearView(): void {
    this.viewContainer.clear();
    this.showingPrimary = false;
    this.showingFallback = false;
  }
}
